/**
 * KAN-757: карточка единой таблицы CRM (вместо блока внимания и групп по
 * дням KAN-756). Счётчики в заголовке: всего строк и сколько ждут внимания.
 * Действия дашборда: у строк внимания "Успех", "Отказ", "Взять в работу"
 * (менеджер сессии KAN-753 или модалка KAN-750), у отработанных только исход.
 * KAN-786: набор кнопок строки даёт rowActions: после выставленного исхода
 * кнопок исхода нет; нажатие проставляет исход в кеше сразу (withCrmRowAction).
 * После действия обновляются доска и блок дашборда (ключ доски вложен в
 * ATTENTION_QUERY_KEY).
 * KAN-760: этапы воронки доски для правки этапа в ячейке.
 * KAN-903: "Успех" открывает оформление заказа клиента (useAttentionSuccess).
 * KAN-766: кнопка "Развернуть"/"Свернуть" справа в заголовке: блок на весь
 * экран (fixed inset-0, z-40: выше шапки и сайдбара, ниже модалок Radix z-50,
 * которые в портале body). Режим, Esc и блокировку прокрутки держит
 * useFullscreenToggle; таблица та же, без ремаунта.
 * KAN-865: действия строки и их модалки в useCrmRowActions (общие с панелью
 * карточки канбан-доски).
 * KAN-880: таблица лидов: счётчик = total ответа (не число загруженных
 * строк), "Показать ещё" (more) дописывает следующую страницу, столбцы
 * группы и дней (columnLabels), действия по applyLeadAction в кеше страниц.
 * Пусто: "Ничего не найдено".
 * KAN-885: groupByDay - заголовки дней (таблица за период; KAN-973: день по period_at).
 * KAN-883: режим "на весь экран" держит CrmTableSection (expanded,
 * onToggleFullscreen): в развёрнутом виде над таблицей та же панель фильтров
 * (toolbar), а пока грузится новый запрос - placeholder вместо таблицы.
 * KAN-898: у строк "В мусор" (ClientJunkRowButton, не у чужого клиента); в режиме мусора (junkMode)
 * только "Вернуть".
 * KAN-906: заголовка "Клиенты N" больше нет: число клиентов и кнопка
 * "Развернуть"/"Свернуть" в строке фильтров (CrmTableCount в CrmTableFilters).
 * KAN-958: скрытые столбцы и кнопка-глаз (columns из useCrmTableColumns секции).
 */
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { ClientJunkRowButton, ClientUnjunkButton } from '@/components/clients/ClientJunkButtons';
import { Card, CardContent } from '@/components/ui/card';
import type { CrmTableColumnsState } from '@/hooks/useCrmTableColumns';
import { applyLeadAction, type CrmLeadsData } from '@/lib/crmLeads';
import type { CrmTableRow } from '@/lib/crmTable';
import type { CrmAttribute, CrmFunnelStage } from '@/types/crm';
import { useCrmRowActions } from './CrmRowActions';
import { CrmTable } from './CrmTable';

/** KAN-766: развёрнутый блок: весь экран, выше шапки/сайдбара, ниже модалок (z-50). */
const FULLSCREEN_CARD = 'fixed inset-0 z-40 overflow-hidden rounded-none';

/** KAN-880: следующая страница таблицы лидов. */
export interface CrmTableMore {
  hasMore: boolean;
  loading: boolean;
  load: () => void;
}

interface CrmBoardTableProps {
  items: CrmTableRow[];
  /** Ключ кеша страниц таблицы лидов (оптимистичное действие строки). */
  queryKey: readonly unknown[];
  /** KAN-880: подписи колонок доски по key (столбец группы). */
  columnLabels: Record<string, string>;
  more: CrmTableMore;
  /** KAN-885: строки по дню периода (KAN-973: period_at, иначе taken_at) с заголовками дней. */
  groupByDay?: boolean;
  /** KAN-760: этапы воронки (board.stages). */
  stages?: readonly CrmFunnelStage[];
  /** KAN-783: столбцы атрибутов лида (board.attributes). */
  attributes?: readonly CrmAttribute[];
  /** KAN-766/883: блок развёрнут на весь экран (режим в useFullscreenToggle секции). */
  expanded: boolean;
  /** KAN-883: панель фильтров внутри развёрнутого блока. */
  toolbar?: ReactNode;
  /** KAN-883: вместо таблицы (загрузка или ошибка нового запроса в развёрнутом виде). */
  placeholder?: ReactNode;
  /** KAN-898: показаны контакты в мусоре: у строк только "Вернуть". */
  junkMode?: boolean;
  /** KAN-958: скрытые столбцы таблицы; не передано - все видны, кнопок-глаз нет. */
  columns?: Pick<CrmTableColumnsState, 'hidden' | 'hide'>;
}

export function CrmBoardTable({
  items,
  queryKey,
  columnLabels,
  more,
  groupByDay = false,
  stages,
  attributes,
  expanded,
  toolbar,
  placeholder,
  junkMode = false,
  columns,
}: CrmBoardTableProps) {
  const rowActions = useCrmRowActions<CrmLeadsData>(queryKey, 'cabinet', applyLeadAction);

  return (
    <Card data-testid="crm-board" data-fullscreen={expanded} className={expanded ? FULLSCREEN_CARD : undefined}>
      {toolbar && <div className="border-b px-3 py-2">{toolbar}</div>}
      <CardContent className="px-0 pb-0">
        {placeholder ? (
          <div className="p-4">{placeholder}</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500" data-testid="crm-empty">
            Ничего не найдено
          </div>
        ) : (
          <CrmTable
            items={items}
            testId="crm-table"
            stages={stages}
            attributes={attributes}
            columnLabels={columnLabels}
            groupByDay={groupByDay}
            fullscreen={expanded}
            hiddenColumns={columns?.hidden}
            onHideColumn={columns?.hide}
            renderActions={(item) =>
              junkMode ? (
                <ClientUnjunkButton clientId={item.row.client_id} testId="crm-row-unjunk" />
              ) : (
                <>
                  {rowActions.render(item)}
                  <ClientJunkRowButton
                    clientId={item.row.client_id}
                    managerId={item.row.manager?.id}
                    testId="crm-row-junk"
                  />
                </>
              )
            }
            footer={
              more.hasMore ? (
                <div className="sticky left-0 flex justify-center py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="crm-leads-more"
                    disabled={more.loading}
                    onClick={more.load}
                  >
                    {more.loading ? 'Загружаем...' : 'Показать ещё'}
                  </Button>
                </div>
              ) : null
            }
          />
        )}
      </CardContent>
      {rowActions.dialogs}
    </Card>
  );
}
